import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { useDebounced } from '../people/usePeople.ts'
import { servicesKeys } from './useServices.ts'
import { useAccess } from '../access/useAccess.ts'

/**
 * PraiseCharts: search their catalog and import songs (lyrics and facts),
 * and — with your own linked account — your purchased chord charts.
 *
 * Keys live under `servicesKeys.all()`, so anything that refreshes the song
 * library refreshes these too.
 */
export const praiseChartsKeys = {
  all: () => [...servicesKeys.all(), 'praisecharts'] as const,
  status: () => [...praiseChartsKeys.all(), 'status'] as const,
  catalog: (q: string) => [...praiseChartsKeys.all(), 'catalog', q] as const,
  library: (q: string) => [...praiseChartsKeys.all(), 'library', q] as const,
  files: (catalogItemId: string, keys: readonly string[]) =>
    [...praiseChartsKeys.all(), 'files', catalogItemId, keys] as const,
  source: (songId: string) => [...praiseChartsKeys.all(), 'source', songId] as const,
}

/** Is it set up on this server, and have *I* linked my account? */
export function usePraiseChartsStatus() {
  const api = useApi()
  // PraiseCharts is part of the song library: Songs Viewer and up (13-ACCESS).
  const library = useAccess().hasSomewhere('songs', 'viewer')
  return useQuery({
    queryKey: praiseChartsKeys.status(),
    enabled: library,
    queryFn: async () => {
      const { data, error } = await api.api.v1.praisecharts.status.get()
      if (error) throw error
      return data
    },
    staleTime: 60_000,
  })
}

/**
 * Linking is OAuth: `start` returns the PraiseCharts page where they approve
 * (the screen sends the browser there); PraiseCharts sends them back to the
 * song library with `?praisecharts=linked` or `failed`.
 */
export function usePraiseChartsLink() {
  const api = useApi()
  const queryClient = useQueryClient()
  const start = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.praisecharts.link.post()
      if (error) throw error
      return data.authorizeUrl
    },
  })
  const unlink = useMutation({
    mutationFn: async () => {
      const { error } = await api.api.v1.praisecharts.link.delete()
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: praiseChartsKeys.all() }),
  })
  return { start, unlink }
}

/** The catalog, as they type (debounced; two letters at least). */
export function usePraiseChartsCatalog(rawQuery: string, enabled = true) {
  const api = useApi()
  const q = useDebounced(rawQuery.trim(), 300)
  const query = useQuery({
    queryKey: praiseChartsKeys.catalog(q),
    enabled: enabled && q.length >= 2,
    queryFn: async () => {
      const { data, error } = await api.api.v1.praisecharts.catalog.get({ query: { q } })
      if (error) throw error
      return data
    },
    staleTime: 60_000,
  })
  return {
    items: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    isSearching: query.isFetching,
    error: query.error,
    searched: q.length >= 2,
  }
}

export type PraiseChartsItem = ReturnType<typeof usePraiseChartsCatalog>['items'][number]

/** A catalog song into the library. Returns the song (the existing one if already imported). */
export function useImportFromPraiseCharts() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (catalogItemId: string) => {
      const { data, error } = await api.api.v1.praisecharts.import.post({ catalogItemId })
      if (error) throw error
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: servicesKeys.all() }),
  })
}

/** My purchases matching a search — only for someone who linked their own account. */
export function usePraiseChartsLibrary(rawQuery: string, enabled = true) {
  const api = useApi()
  const q = useDebounced(rawQuery.trim(), 300)
  const query = useQuery({
    queryKey: praiseChartsKeys.library(q),
    enabled: enabled && q.length >= 1,
    queryFn: async () => {
      const { data, error } = await api.api.v1.praisecharts.library.get({ query: { q } })
      if (error) throw error
      return data
    },
    staleTime: 60_000,
  })
  return {
    owned: query.data?.owned ?? [],
    other: query.data?.other ?? [],
    isSearching: query.isFetching,
    error: query.error,
  }
}

/** The files I can use for one song, by key. */
export function usePraiseChartsFiles(catalogItemId: string, keys: readonly string[]) {
  const api = useApi()
  return useQuery({
    queryKey: praiseChartsKeys.files(catalogItemId, keys),
    enabled: catalogItemId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.praisecharts
        .library({ catalogItemId })
        .files.get({ query: { keys: [...keys] } })
      if (error) throw error
      return data
    },
  })
}

/** My purchased chart, in a key, onto a song (or a new song). */
export function useImportPraiseChartsChords() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { catalogItemId: string; key: string; songId?: string }) => {
      const { data, error } = await api.api.v1.praisecharts.library.import.post(input)
      if (error) throw error
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: servicesKeys.all() }),
  })
}

/** "From PraiseCharts": where an imported song came from (null for the rest). */
export function useSongSource(songId: string) {
  const api = useApi()
  return useQuery({
    queryKey: praiseChartsKeys.source(songId),
    enabled: songId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.praisecharts.songs({ songId }).source.get()
      if (error) throw error
      return data.source
    },
    staleTime: 5 * 60_000,
  })
}
